import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPlanWithWeeks, getSessionTypes } from '../../api/athlete';
import { useAuth } from '../../contexts/AuthContext';
import { SessionType } from '../../types/athlete';
import { ArrowLeft, ArrowRight, Check, Calendar } from 'lucide-react';

type WizardStep = 1 | 2 | 3 | 4 | 5;

interface PlanData {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
}

interface WeeklySchedule {
  [weekNumber: number]: {
    [dayOfWeek: number]: {
      sessionTypeId: string;
      scheduledDate: string;
      duration: number;
    };
  };
}

const daysOfWeek = [
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
  { value: 7, label: 'Sunday', short: 'Sun' },
];

const TrainingPlanWizard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<WizardStep>(1);
  const [loading, setLoading] = useState(false);
  const [sessionTypes, setSessionTypes] = useState<SessionType[]>([]);

  // Step 1: Plan Basics
  const [planData, setPlanData] = useState<PlanData>({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
  });

  // Step 2: Weekly Frequency
  const [weeklyFrequency, setWeeklyFrequency] = useState({
    swim: 0,
    bike: 0,
    run: 0,
    strength: 0,
  });

  // Step 3: Session Types per day
  const [schedule, setSchedule] = useState<WeeklySchedule>({});

  // Calculate total weeks
  const totalWeeks = planData.startDate && planData.endDate
    ? Math.ceil((new Date(planData.endDate).getTime() - new Date(planData.startDate).getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1
    : 0;

  useEffect(() => {
    loadSessionTypes();
  }, []);

  const loadSessionTypes = async () => {
    const types = await getSessionTypes();
    setSessionTypes(types);
  };

  const handleNext = () => {
    if (step < 5) {
      setStep((step + 1) as WizardStep);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep((step - 1) as WizardStep);
    }
  };

  const handleSubmit = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const sessions: Array<{
        week_number: number;
        day_of_week: number;
        session_type_id: string;
        scheduled_date: string;
        planned_duration_minutes: number;
      }> = [];

      // Generate sessions for each week
      for (let weekNum = 1; weekNum <= totalWeeks; weekNum++) {
        const weekSchedule = schedule[weekNum] || {};
        for (const [dayStr, sessionData] of Object.entries(weekSchedule)) {
          const dayOfWeek = parseInt(dayStr);
          sessions.push({
            week_number: weekNum,
            day_of_week: dayOfWeek,
            session_type_id: sessionData.sessionTypeId,
            scheduled_date: sessionData.scheduledDate,
            planned_duration_minutes: sessionData.duration,
          });
        }
      }

      await createPlanWithWeeks(user.id, {
        name: planData.name,
        description: planData.description,
        start_date: planData.startDate,
        end_date: planData.endDate,
      }, sessions);

      navigate('/plans');
    } catch (error) {
      console.error('Error creating plan:', error);
    } finally {
      setLoading(false);
    }
  };

  // Render functions for each step
  const renderStep1 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Step 1: Plan Basics</h2>
      <p className="text-muted">Give your training plan a name and set the date range.</p>

      <div>
        <label className="block text-sm font-medium text-[#e2e8f0] mb-2">Plan Name</label>
        <input
          type="text"
          placeholder="e.g., Ironman 70.3 Training"
          value={planData.name}
          onChange={(e) => setPlanData({ ...planData, name: e.target.value })}
          className="w-full"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-[#e2e8f0] mb-2">Description (optional)</label>
        <textarea
          placeholder="Describe your training goals..."
          value={planData.description}
          onChange={(e) => setPlanData({ ...planData, description: e.target.value })}
          className="w-full h-24 resize-none"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-[#e2e8f0] mb-2">Start Date</label>
          <input
            type="date"
            value={planData.startDate}
            onChange={(e) => setPlanData({ ...planData, startDate: e.target.value })}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#e2e8f0] mb-2">End Date</label>
          <input
            type="date"
            value={planData.endDate}
            onChange={(e) => setPlanData({ ...planData, endDate: e.target.value })}
            className="w-full"
          />
        </div>
      </div>

      {totalWeeks > 0 && (
        <div className="card-base p-4 bg-[rgba(59,130,246,0.1)] border border-[rgba(59,130,246,0.2)]">
          <p className="font-medium text-[#3b82f6]">Total Duration: {totalWeeks} weeks</p>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Step 2: Weekly Frequency</h2>
      <p className="text-muted">How many sessions of each discipline do you want per week?</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          { key: 'swim', label: 'Swim Sessions', color: 'blue' },
          { key: 'bike', label: 'Bike Sessions', color: 'green' },
          { key: 'run', label: 'Run Sessions', color: 'orange' },
          { key: 'strength', label: 'Strength Sessions', color: 'purple' },
        ].map((item) => (
          <div key={item.key} className="card-base p-6">
            <label className="block text-sm font-medium text-[#e2e8f0] mb-4">{item.label}</label>
            <input
              type="range"
              min="0"
              max="7"
              value={weeklyFrequency[item.key as keyof typeof weeklyFrequency]}
              onChange={(e) => setWeeklyFrequency({ ...weeklyFrequency, [item.key]: parseInt(e.target.value) })}
              className="w-full mb-2"
            />
            <p className="text-center text-2xl font-bold">
              {weeklyFrequency[item.key as keyof typeof weeklyFrequency]} sessions/week
            </p>
          </div>
        ))}
      </div>

      <div className="card-base p-4">
        <p className="font-medium mb-2">Weekly Breakdown:</p>
        <p className="text-muted">
          Total sessions per week:{' '}
          <span className="font-bold text-[#3b82f6]">
            {Object.values(weeklyFrequency).reduce((a, b) => a + b, 0)}
          </span>
        </p>
      </div>
    </div>
  );

  const renderStep3 = () => {
    const totalSessions = Object.values(weeklyFrequency).reduce((a, b) => a + b, 0);

    if (totalSessions === 0) {
      return (
        <div className="text-center py-12">
          <p className="text-muted mb-4">No sessions scheduled yet.</p>
          <button onClick={handleBack} className="text-[#3b82f6] hover:underline">
            Go back and add sessions
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold">Step 3: Assign Session Types</h2>
        <p className="text-muted">Select session types for your weekly schedule. Click on each day to assign a session.</p>

        <div className="card-base p-6 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left py-3 px-4 text-muted font-medium">Week</th>
                {daysOfWeek.map(day => (
                  <th key={day.value} className="text-center py-3 px-2 text-muted font-medium">{day.short}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: Math.min(totalWeeks, 4) }).map((_, weekIndex) => {
                const weekNum = weekIndex + 1;
                return (
                  <tr key={weekNum}>
                    <td className="py-3 px-4 font-medium">Week {weekNum}</td>
                    {daysOfWeek.map(day => {
                      const existingSession = schedule[weekNum]?.[day.value];
                      return (
                        <td key={day.value} className="py-2 px-2">
                          <select
                            value={existingSession?.sessionTypeId || ''}
                            onChange={(e) => {
                              const sessionTypeId = e.target.value;
                              if (!sessionTypeId) {
                                const newSchedule = { ...schedule };
                                if (newSchedule[weekNum]) {
                                  delete newSchedule[weekNum][day.value];
                                }
                                setSchedule(newSchedule);
                                return;
                              }

                              const sessionType = sessionTypes.find(s => s.id === sessionTypeId);
                              const duration = sessionType?.default_duration_minutes || 60;
                              const weekStartDate = new Date(planData.startDate);
                              weekStartDate.setDate(weekStartDate.getDate() + (weekNum - 1) * 7);
                              const sessionDate = new Date(weekStartDate);
                              sessionDate.setDate(sessionDate.getDate() + day.value - 1);

                              setSchedule(prev => ({
                                ...prev,
                                [weekNum]: {
                                  ...(prev[weekNum] || {}),
                                  [day.value]: {
                                    sessionTypeId,
                                    scheduledDate: sessionDate.toISOString().split('T')[0],
                                    duration,
                                  },
                                },
                              }));
                            }}
                            className="w-full text-sm"
                          >
                            <option value="">-</option>
                            {sessionTypes.map(type => (
                              <option key={type.id} value={type.id}>
                                {type.name}
                              </option>
                            ))}
                          </select>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {totalWeeks > 4 && (
            <p className="text-center text-muted mt-4 text-sm">
              ... and {totalWeeks - 4} more weeks
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderStep4 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Step 4: Review & Adjust</h2>
      <p className="text-muted">Review your plan schedule. You can adjust dates and durations below.</p>

      <div className="space-y-4">
        {Object.entries(schedule).slice(0, 2).map(([weekNum, weekSessions]) => (
          <div key={weekNum} className="card-base p-4">
            <h3 className="font-semibold mb-3">Week {weekNum}</h3>
            <div className="space-y-2">
              {Object.entries(weekSessions).map(([dayStr, sessionData]) => {
                const dayOfWeek = parseInt(dayStr);
                const day = daysOfWeek.find(d => d.value === dayOfWeek);
                const session = sessionData as { sessionTypeId: string; scheduledDate: string; duration: number };
                const sessionType = sessionTypes.find(s => s.id === session.sessionTypeId);
                return (
                  <div key={dayStr} className="flex items-center justify-between py-2 border-b border-[#2d3748] last:border-0">
                    <div className="flex items-center space-x-3">
                      <span className="text-muted w-20">{day?.label}</span>
                      <span className="font-medium">{sessionType?.name}</span>
                    </div>
                      <div className="flex items-center space-x-4">
                        <input
                          type="date"
                          value={session.scheduledDate}
                          onChange={(e) => {
                            setSchedule(prev => ({
                              ...prev,
                              [weekNum]: {
                                ...(prev[parseInt(weekNum)] || {}),
                                [dayStr]: { ...session, scheduledDate: e.target.value },
                              },
                            }));
                          }}
                          className="w-40 text-sm"
                        />
                        <input
                          type="number"
                          value={session.duration}
                          onChange={(e) => {
                            setSchedule(prev => ({
                              ...prev,
                              [weekNum]: {
                                ...(prev[parseInt(weekNum)] || {}),
                                [dayStr]: { ...session, duration: parseInt(e.target.value) },
                              },
                            }));
                          }}
                        className="w-20 text-sm"
                        min="15"
                        max="300"
                      />
                      <span className="text-muted text-sm">min</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep5 = () => {
    const sessionCount = Object.values(schedule).reduce((acc, week) => {
      return acc + Object.keys(week).length;
    }, 0);

    const disciplineBreakdown = Object.entries(schedule).reduce((acc, [, weekSessions]) => {
      Object.values(weekSessions).forEach((session) => {
        const s = session as { sessionTypeId: string };
        const type = sessionTypes.find(st => st.id === s.sessionTypeId);
        if (type) {
          acc[type.discipline] = (acc[type.discipline] || 0) + 1;
        }
      });
      return acc;
    }, {} as Record<string, number>);

    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold">Step 5: Create Your Plan</h2>
        <p className="text-muted">Review your plan summary before creating it.</p>

        <div className="card-base p-6">
          <h3 className="font-semibold text-lg mb-4">{planData.name}</h3>
          <p className="text-muted mb-4">{planData.description || 'No description'}</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-[#3b82f6]">{totalWeeks}</p>
              <p className="text-sm text-muted">Weeks</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-[#3b82f6]">{sessionCount}</p>
              <p className="text-sm text-muted">Total Sessions</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-green-400">{disciplineBreakdown.bike || 0}</p>
              <p className="text-sm text-muted">Bike Sessions</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-orange-400">{disciplineBreakdown.run || 0}</p>
              <p className="text-sm text-muted">Run Sessions</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <Calendar size={16} className="text-muted" />
              <span>{planData.startDate} → {planData.endDate}</span>
            </div>
          </div>
        </div>

        <div className="bg-[rgba(59,130,246,0.1)] border border-[rgba(59,130,246,0.2)] rounded-lg p-4">
          <p className="text-sm text-[#e2e8f0]">
            Your plan will be created with {totalWeeks} weeks of training. You can edit sessions after creation.
          </p>
        </div>
      </div>
    );
  };

  const canProceed = () => {
    switch (step) {
      case 1: return planData.name && planData.startDate && planData.endDate && totalWeeks > 0;
      case 2: return Object.values(weeklyFrequency).some(v => v > 0);
      case 3: return Object.keys(schedule).length > 0;
      case 4: return true;
      case 5: return true;
      default: return false;
    }
  };

  return (
    <div>
      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8">
        {[1, 2, 3, 4, 5].map((s) => (
          <div
            key={s}
            className={`flex items-center ${s < 5 ? 'flex-1' : ''}`}
          >
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                step >= s
                  ? 'bg-[#3b82f6] text-white'
                  : 'bg-[#141921] text-[#718096]'
              }`}
            >
              {step > s ? <Check size={20} /> : s}
            </div>
            {s < 5 && (
              <div
                className={`flex-1 h-1 mx-2 ${
                  step > s ? 'bg-[#3b82f6]' : 'bg-[#141921]'
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="card-base p-8 mb-8">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
        {step === 5 && renderStep5()}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={step === 1}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
            step === 1
              ? 'opacity-0 pointer-events-none'
              : 'bg-[#141921] text-[#e2e8f0] hover:bg-[#2d3748]'
          }`}
        >
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>

        {step < 5 ? (
          <button
            onClick={handleNext}
            disabled={!canProceed()}
            className={`flex items-center space-x-2 px-6 py-2 rounded-lg transition-colors ${
              canProceed()
                ? 'bg-[#3b82f6] text-white hover:bg-[#60a5fa]'
                : 'bg-[#141921] text-[#718096] cursor-not-allowed'
            }`}
          >
            <span>Next</span>
            <ArrowRight size={18} />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex items-center space-x-2 px-6 py-2 bg-[#3b82f6] text-white rounded-lg hover:bg-[#60a5fa] transition-colors disabled:opacity-50"
          >
            <span>{loading ? 'Creating...' : 'Create Plan'}</span>
            <Check size={18} />
          </button>
        )}
      </div>
    </div>
  );
};

export default TrainingPlanWizard;
